export class IdentitySessionError extends Error {
    httpStatus;
    apiError;
    constructor(httpStatus, apiError) {
        const suffix = apiError ? `|${apiError}` : "";
        super(`identity_http_${httpStatus}${suffix}`);
        this.name = "IdentitySessionError";
        this.httpStatus = httpStatus;
        this.apiError = apiError;
    }
}
async function readApiError(res) {
    try {
        const j = (await res.json());
        return typeof j.error === "string" && j.error.length > 0 ? j.error : undefined;
    }
    catch {
        return undefined;
    }
}
export function createIdentityClient(fetcher) {
    return {
        async begin(getToken) {
            const res = await fetcher(getToken, "/api/v1/me/identity", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ gaveConsent: true }),
            });
            if (!res.ok) {
                throw new IdentitySessionError(res.status, await readApiError(res));
            }
            return (await res.json());
        },
        async complete(getToken, applicantId) {
            const res = await fetcher(getToken, "/api/v1/me/identity/complete", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ applicantId }),
            });
            if (!res.ok) {
                throw new IdentitySessionError(res.status, await readApiError(res));
            }
            return (await res.json());
        },
    };
}
/** Maps `begin` / `complete` / network failures to user-facing copy. */
export function formatIdentityError(err) {
    if (!(err instanceof Error)) {
        return "Identity verification failed. Try again.";
    }
    if (err.message === "clerk_session_missing") {
        return "Sign in again — your session token was missing.";
    }
    if (err instanceof IdentitySessionError) {
        const { httpStatus, apiError } = err;
        if (apiError === "identity_verification_not_configured") {
            return "Identity verification is not configured on the API. Contact support.";
        }
        if (apiError === "identity_verification_pending") {
            return "Your verification has not finished yet. Complete the steps in the verification window, then tap “I finished verification” again.";
        }
        if (apiError === "identity_verification_not_successful") {
            return "The verification did not pass. Start again from “Begin verification” and retake the photos in good light.";
        }
        if (apiError === "identity_document_invalid") {
            return "We could not confirm that ID as genuine. Start again and retake the photos, or contact support.";
        }
        if (apiError === "identity_document_expired") {
            return "That ID has expired. Colorado delivery requires a current, unexpired government ID.";
        }
        if (apiError === "identity_document_missing_dob") {
            return "We could not read the date of birth on that ID. Start again and retake the photos.";
        }
        if (apiError === "under_21") {
            return "Verification succeeded but the date of birth on your ID does not meet the 21+ requirement for Colorado delivery.";
        }
        if (apiError === "identity_applicant_mismatch") {
            return "That verification session does not match your account. Start again from “Begin verification”.";
        }
        if (httpStatus === 401) {
            return "The API rejected your sign-in (401). Sign in again.";
        }
        if (httpStatus === 403) {
            return "You are not allowed to run identity verification for this account (403).";
        }
        if (httpStatus === 409) {
            return "Verification is still in progress. Finish in the verification window, then try again.";
        }
        if (httpStatus === 502) {
            return "The verification service could not be reached (502). Try again in a moment.";
        }
        if (httpStatus === 503) {
            return "The API is not configured for identity verification (503). Contact support.";
        }
        if (httpStatus >= 500) {
            return `The API returned an error (HTTP ${httpStatus}). Try again.`;
        }
        if (apiError) {
            return `Verification request failed (${apiError}). Try again.`;
        }
        return `Verification request failed (HTTP ${httpStatus}). Try again.`;
    }
    if (err.message.includes("Failed to fetch") || err.name === "TypeError") {
        return "Could not reach the API (network error). Check your connection.";
    }
    return err.message || "Identity verification failed. Try again.";
}
//# sourceMappingURL=identity.js.map